import React from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    FormLabel,
    InputLabel,
    Link,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    AnswerTypeFrontend,
    QuestionOptionDtoFrontend,
    QuestionTypeFrontend,
    SessionQuestionEntityFrontend,
} from '../../../api/sessionQuestionsApi';
import { PublicSessionFileType } from '../../../api/publicSessionApi';
import { FileUploadField } from './FileUploadField';
import { ProfessionPicker } from './ProfessionPicker';
import { SuggestionValuePreview } from '../sections/SuggestionValuePreview';
import { UploadEntry } from '../hooks/usePublicShareUploads';
import { TranslateFn } from '../hooks/useSessionFormI18n';

/** Sentinel value used on the FINAL_PROFESSION_CONFIRMATION composite widget
 *  when the worker explicitly says "no other professions". Stored on the
 *  session_question's answer so the recruiter sees a deterministic value
 *  instead of an empty string. */
const NO_OTHER_PROFESSIONS = 'no';

export interface QuestionFieldProps {
    question: SessionQuestionEntityFrontend;
    value: unknown;
    onValueChange: (questionId: number, value: any) => void;
    onBlur: (question: SessionQuestionEntityFrontend) => void;
    onImmediateChange: (
        question: SessionQuestionEntityFrontend,
        newValue: string,
    ) => Promise<void> | void;
    onOpenListModal: (question: SessionQuestionEntityFrontend) => void;
    /** Uploads tracked across the form so inline FILE widgets can show "already uploaded" state. */
    uploads: UploadEntry[];
    onUpload: (file: File, fileType: PublicSessionFileType) => Promise<UploadEntry>;
    /**
     * Profession choices, sourced from the PROFESSION_SELECTION question's
     * `options`. Forwarded down so the FINAL_PROFESSION_CONFIRMATION
     * "Other professions?" composite can reuse the same dropdown options
     * even though its own question definition carries none. The orchestrator
     * is responsible for filtering out professions the worker already has
     * (so the Yes-branch dropdown only offers genuinely-new choices).
     */
    professionOptions: Array<Pick<QuestionOptionDtoFrontend, 'key' | 'label'>>;
    /**
     * Add a profession to the worker via the public-share form's add path.
     * The orchestrator owns the API call + the `injectWrapped` of the
     * refreshed `{ worker, questions }`; this prop is just the boundary.
     * Called by the FINAL_PROFESSION_CONFIRMATION Yes-branch picker after
     * the worker picks an additional profession.
     */
    onAddProfession: (professionKey: string) => Promise<void>;
    /**
     * True while an add-profession request is in flight. Used to disable
     * the picker so rapid clicks don't fire multiple concurrent adds.
     */
    isAddingProfession: boolean;
    t: TranslateFn;
}

/**
 * Dispatches to the right field renderer based on `answerType`. Behavior is a
 * straight extraction from the old `renderFormField` switch — no semantic
 * changes here, just split out so each answer type has a tighter render scope
 * and the orchestrator stays small.
 */
export const QuestionField: React.FC<QuestionFieldProps> = ({
    question,
    value,
    onValueChange,
    onBlur,
    onImmediateChange,
    onOpenListModal,
    uploads,
    onUpload,
    professionOptions,
    onAddProfession,
    isAddingProfession,
    t,
}) => {
    const isRequired = question.isRequiredAnswer;
    const isConsentQuestion = question.questionKey === 'ACTION_CONSENT';
    const fieldLabel =
        isConsentQuestion && question.privacyPolicyUrl
            ? (question.questionShorten || question.question)
                  .replace(question.privacyPolicyUrl, '')
                  .trim()
            : question.questionShorten || question.question;

    // questionType-driven special cases must come BEFORE the answerType
    // switch — both of these would otherwise fall into the default RADIO /
    // TEXT renderers and lose the structured behaviour they need.

    // PROFESSION_SELECTION → reusable dropdown. The seed lists ~10
    // professions; a vertical RadioGroup of that many options bloated the
    // form. Dropdown stays compact and shares the picker UI with the
    // FINAL_PROFESSION_CONFIRMATION composite below.
    if (question.questionType === QuestionTypeFrontend.PROFESSION_SELECTION) {
        const opts = (question.options ?? []).map((o) => ({ key: o.key, label: o.label }));
        return (
            <ProfessionPicker
                idBase={`profession-picker-${question.id}`}
                options={opts}
                value={typeof value === 'string' ? value : ''}
                onChange={async (next) => {
                    onValueChange(question.id, next);
                    await onImmediateChange(question, next);
                }}
                label={question.questionShorten || question.question}
                placeholder={t('profession_picker_placeholder')}
                required={isRequired}
            />
        );
    }

    // FINAL_PROFESSION_CONFIRMATION → composite widget. Bot side has a
    // classifier that detects "I also do plastering" and runs the
    // ADD_PROFESSION_INTENT applier. The form has no classifier, so we
    // hand the worker a Yes/No radio and (on Yes) a profession dropdown,
    // then call the dedicated `/professions/add` endpoint to mirror what
    // the bot does — activate worker_professions, seed the new profession's
    // question block, reset this question's gate.
    //
    // Storage convention on this question itself:
    //   - "No"           → persisted as the sentinel `"no"`. Validation passes.
    //   - Profession pick → does NOT write to this question's answer; the
    //                       backend reset-gate flow leaves it unanswered so
    //                       the worker can pick yet another profession.
    //                       Required-field validation kicks in on submit if
    //                       the worker leaves the radio empty.
    if (question.questionType === QuestionTypeFrontend.FINAL_PROFESSION_CONFIRMATION) {
        const stringValue = typeof value === 'string' ? value : '';
        const isNo = stringValue === NO_OTHER_PROFESSIONS;
        const yesNoValue = isNo ? 'no' : stringValue === 'yes' ? 'yes' : '';

        return (
            <Stack spacing={1.5}>
                <FormControl component="fieldset" required={isRequired} disabled={isAddingProfession}>
                    <FormLabel>{question.questionShorten || question.question}</FormLabel>
                    <RadioGroup
                        row
                        value={yesNoValue}
                        onChange={async (_event, newRadio) => {
                            if (newRadio === 'no') {
                                onValueChange(question.id, NO_OTHER_PROFESSIONS);
                                await onImmediateChange(question, NO_OTHER_PROFESSIONS);
                            } else {
                                // "Yes" alone is just an intent — we mark the
                                // local form state as 'yes' so the dropdown
                                // shows, but persist nothing until the worker
                                // either picks a profession (triggers add) or
                                // flips back to No. Required-field validation
                                // surfaces an error if they submit without
                                // resolving one of these.
                                onValueChange(question.id, 'yes');
                            }
                        }}
                    >
                        <FormControlLabel value="yes" control={<Radio />} label={t('answer_yes')} />
                        <FormControlLabel value="no" control={<Radio />} label={t('answer_no')} />
                    </RadioGroup>
                </FormControl>
                {yesNoValue === 'yes' && (
                    <ProfessionPicker
                        idBase={`other-profession-picker-${question.id}`}
                        options={professionOptions}
                        value=""
                        onChange={async (next) => {
                            // Fire the dedicated add-profession endpoint.
                            // After success the orchestrator injects the
                            // refreshed wrapped { worker, questions } — the
                            // new profession's question block appears above
                            // this gate, and the gate's `cv_prefilled` state
                            // resets so the radio comes back to the empty
                            // ("any more?") state, ready for another add.
                            await onAddProfession(next);
                        }}
                        placeholder={t('profession_picker_placeholder')}
                        label={t('profession_picker_label')}
                        required={isRequired}
                        disabled={isAddingProfession}
                    />
                )}
            </Stack>
        );
    }

    switch (question.answerType) {
        case AnswerTypeFrontend.TEXT:
        case AnswerTypeFrontend.TEXT_AREA:
            return (
                <TextField
                    fullWidth
                    multiline={question.answerType === AnswerTypeFrontend.TEXT_AREA}
                    minRows={question.answerType === AnswerTypeFrontend.TEXT_AREA ? 3 : 1}
                    label={question.questionShorten || question.question}
                    value={typeof value === 'string' ? value : ''}
                    onChange={(event) => onValueChange(question.id, event.target.value)}
                    onBlur={() => onBlur(question)}
                    required={isRequired}
                />
            );

        case AnswerTypeFrontend.NUMBER:
            return (
                <TextField
                    fullWidth
                    type="number"
                    label={question.questionShorten || question.question}
                    value={typeof value === 'string' ? value : ''}
                    onChange={(event) => onValueChange(question.id, event.target.value)}
                    onBlur={() => onBlur(question)}
                    required={isRequired}
                />
            );

        case AnswerTypeFrontend.DATE:
            return (
                <TextField
                    fullWidth
                    type="date"
                    label={question.questionShorten || question.question}
                    value={typeof value === 'string' ? value : ''}
                    onChange={(event) => onValueChange(question.id, event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    onBlur={() => onBlur(question)}
                    required={isRequired}
                />
            );

        case AnswerTypeFrontend.BOOLEAN:
            return (
                <FormControl component="fieldset" required={isRequired}>
                    <FormLabel>{fieldLabel}</FormLabel>
                    {isConsentQuestion && question.privacyPolicyUrl && (
                        <Link
                            href={question.privacyPolicyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ alignSelf: 'flex-start', mt: 0.5, mb: 0.5 }}
                        >
                            {question.privacyPolicyUrl}
                        </Link>
                    )}
                    <RadioGroup
                        row
                        value={typeof value === 'string' ? value : ''}
                        onChange={async (_event, newValue) => {
                            onValueChange(question.id, newValue);
                            // Consent is a terminal decision. Keep it local until
                            // final-submit validation has passed so choosing an
                            // option cannot close the public-share session while
                            // another required field is still missing.
                            if (!isConsentQuestion) {
                                await onImmediateChange(question, newValue);
                            }
                        }}
                    >
                        {isConsentQuestion ? (
                            (question.options ?? []).map((option) => (
                                <FormControlLabel
                                    key={option.key}
                                    value={option.key}
                                    control={<Radio />}
                                    label={option.label}
                                />
                            ))
                        ) : (
                            <>
                                <FormControlLabel value="true" control={<Radio />} label={t('answer_yes')} />
                                <FormControlLabel value="false" control={<Radio />} label={t('answer_no')} />
                            </>
                        )}
                    </RadioGroup>
                </FormControl>
            );

        case AnswerTypeFrontend.RADIO:
            return (
                <FormControl component="fieldset" required={isRequired}>
                    <FormLabel>{question.questionShorten || question.question}</FormLabel>
                    <RadioGroup
                        value={typeof value === 'string' ? value : ''}
                        onChange={async (_event, newValue) => {
                            onValueChange(question.id, newValue);
                            await onImmediateChange(question, newValue);
                        }}
                    >
                        {(question.options || []).map((option) => (
                            <FormControlLabel
                                key={option.key}
                                value={option.key}
                                control={<Radio />}
                                label={option.label}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            );

        case AnswerTypeFrontend.MULTISELECT: {
            const selectedValues = Array.isArray(value) ? value : [];
            return (
                <FormControl fullWidth required={isRequired}>
                    <InputLabel id={`multiselect-${question.id}`}>
                        {question.questionShorten || question.question}
                    </InputLabel>
                    <Select
                        labelId={`multiselect-${question.id}`}
                        multiple
                        value={selectedValues}
                        label={question.questionShorten || question.question}
                        onChange={(event) => {
                            const newValue =
                                typeof event.target.value === 'string'
                                    ? event.target.value.split(',').filter(Boolean)
                                    : event.target.value;
                            onValueChange(question.id, newValue);
                        }}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(selected as string[]).map((val) => {
                                    const display =
                                        question.options?.find((opt) => opt.key === val)?.label ||
                                        val;
                                    return <Chip key={val} label={display} />;
                                })}
                            </Box>
                        )}
                    >
                        {(question.options || []).map((option) => (
                            <MenuItem key={option.key} value={option.key}>
                                <Checkbox checked={selectedValues.indexOf(option.key) > -1} />
                                <Typography>{option.label}</Typography>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        }

        case AnswerTypeFrontend.LIST_OBJECT: {
            const currentValue = typeof value === 'string' ? value : '[]';
            let itemCount = 0;
            try {
                const parsed = JSON.parse(currentValue);
                if (Array.isArray(parsed)) {
                    itemCount = parsed.length;
                }
            } catch {
                itemCount = 0;
            }
            // When items exist, show them properly using the same key-value
            // card renderer the AI banner uses for object arrays. Worker sees
            // the actual content (Company / Position / Start Date / etc.)
            // instead of an opaque "1 items added" pill. Edit button stays
            // for add/edit/remove via the dedicated modal.
            return (
                <Stack spacing={1.5}>
                    <Typography variant="subtitle1">
                        {question.questionShorten || question.question}
                    </Typography>
                    {itemCount > 0 ? (
                        <>
                            <SuggestionValuePreview value={currentValue} />
                            <Typography variant="caption" color="text.secondary">
                                {t('list_object_items_added', { count: itemCount })}
                            </Typography>
                        </>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            {t('list_object_no_items')}
                        </Typography>
                    )}
                    <Box>
                        <Button variant="outlined" onClick={() => onOpenListModal(question)}>
                            {t('list_object_edit')}
                        </Button>
                    </Box>
                </Stack>
            );
        }

        case AnswerTypeFrontend.FILE:
            return (
                <FileUploadField
                    question={question}
                    uploads={uploads}
                    onUpload={onUpload}
                    t={t}
                />
            );

        default:
            return (
                <TextField
                    fullWidth
                    label={question.questionShorten || question.question}
                    value={typeof value === 'string' ? value : ''}
                    onChange={(event) => onValueChange(question.id, event.target.value)}
                />
            );
    }
};
